import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useMemo, Children, createRef, useState, useEffect } from 'react';
import { v4 } from 'uuid';
import { useListKeyboardNavigation } from '../../utils/hooks/useListKeyboardNavigation.js';

var _excluded = ["children", "expanded", "id", "title"];

var mapRegions = function mapRegions(children) {
  return Children.toArray(children).filter(function (child) {
    return !!child;
  }).map(function (child) {
    var _child$props = child.props,
        children = _child$props.children,
        _child$props$expanded = _child$props.expanded,
        isExpanded = _child$props$expanded === void 0 ? false : _child$props$expanded,
        nativeId = _child$props.id,
        title = _child$props.title,
        props = _objectWithoutPropertiesLoose(_child$props, _excluded);

    var id = nativeId || v4();
    var regionId = id + "-region";
    return {
      ref: createRef(),
      trigger: _extends({}, props, {
        'aria-controls': regionId,
        children: title,
        id: id,
        type: 'button'
      }),
      region: {
        'aria-labelledby': id,
        children: children,
        id: regionId,
        role: 'region'
      },
      isExpanded: isExpanded,
      isFocused: false
    };
  });
};

var getInitialExpansionState = function getInitialExpansionState(cachedRegions, allowMultipleExpanded) {
  var _ref;

  if (allowMultipleExpanded) {
    if (cachedRegions.some(function (region) {
      return region.isExpanded;
    })) {
      return cachedRegions.reduce(function (state, region, index) {
        var _extends2;

        return _extends({}, state, (_extends2 = {}, _extends2[index] = !!region.isExpanded, _extends2));
      }, {});
    }

    return {};
  }

  var firstExpandedRegion = cachedRegions.findIndex(function (region) {
    return region.isExpanded !== false;
  });
  return _ref = {}, _ref[firstExpandedRegion > -1 ? firstExpandedRegion : 0] = true, _ref;
};

var useAccordion = function useAccordion(children, allowMultipleExpanded) {
  if (allowMultipleExpanded === void 0) {
    allowMultipleExpanded = false;
  }

  var cachedRegions = useMemo(function () {
    return mapRegions(children);
  }, [children]);
  var initialExpandedState = useMemo(function () {
    return getInitialExpansionState(cachedRegions, allowMultipleExpanded);
  }, [cachedRegions, allowMultipleExpanded]);

  var _React$useState = useState(initialExpandedState),
      expanded = _React$useState[0],
      setExpanded = _React$useState[1];

  var _useListKeyboardNavig = useListKeyboardNavigation(cachedRegions.length, {
    initialIndex: undefined
  }),
      currentRegionIndex = _useListKeyboardNavig[0],
      setRegionIndex = _useListKeyboardNavig[1],
      getRegionIndexFromKeycode = _useListKeyboardNavig[2];

  useEffect(function () {
    if (currentRegionIndex !== undefined) {
      var _cachedRegions$curren;

      var activeRegionRef = (_cachedRegions$curren = cachedRegions[currentRegionIndex]) === null || _cachedRegions$curren === void 0 ? void 0 : _cachedRegions$curren.ref;

      if (activeRegionRef.current) {
        activeRegionRef.current.focus();
      }
    }
  }, [currentRegionIndex]);

  var handleClick = function handleClick(index) {
    setRegionIndex(index);
    setExpanded(function (currentExpandedRegions) {
      var isExpanded = expanded[index];

      if (allowMultipleExpanded) {
        var _extends3;

        return _extends({}, currentExpandedRegions, (_extends3 = {}, _extends3[index] = !isExpanded, _extends3));
      } else if (!isExpanded) {
        var _ref2;

        return _ref2 = {}, _ref2[index] = true, _ref2;
      }

      return currentExpandedRegions;
    });
  };

  var handleKeyDown = function handleKeyDown(event) {
    var nextIndex = getRegionIndexFromKeycode(event);

    if (nextIndex !== undefined) {
      setRegionIndex(nextIndex);
    }
  };

  return cachedRegions.map(function (region, index) {
    var isExpanded = !!expanded[index];
    var isDisabled = !allowMultipleExpanded && isExpanded;
    return _extends({}, region, {
      trigger: _extends({}, region.trigger, {
        'aria-disabled': isDisabled ? 'true' : 'false',
        'aria-expanded': isExpanded ? 'true' : 'false',
        onClick: function onClick() {
          return handleClick(index);
        },
        onFocus: function onFocus() {
          return setRegionIndex(index);
        },
        onKeyDown: handleKeyDown,
        type: 'button'
      }),
      region: _extends({}, region.region, {
        hidden: !isExpanded
      }),
      isExpanded: isExpanded,
      isFocused: index === currentRegionIndex
    });
  });
};

export { useAccordion };
//# sourceMappingURL=useAccordion.js.map
